/**
 * Form for manually overriding patient engagement status.
 * @agentic connections
 */

// Components
import { Stack, Button, Group, Text, Alert, Select } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { FormField } from "@benchmark/ui/components/atomic";

// Hooks
import { useState, useCallback, useEffect, startTransition } from "react";
import { useEngagementStatus } from "@/hooks/domain/useEngagementStatus";

// Types
import type { Patient } from "@medplum/fhirtypes";
import type { PatientEngagementStatusCode } from "@benchmark/core/constants";

// Utils
import {
  PATIENT_ENGAGEMENT_STATUS_SELECT_OPTIONS,
  getPatientEngagementStatusDisplay,
} from "@benchmark/core/constants";

// Interfaces
/**
 * Props for the engagement status form.
 * @agentic connections
 */
export type EngagementStatusFormProps = {
  patient: Patient;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/**
 * Form for manually overriding patient engagement status.
 * @agentic connections
 */
export const EngagementStatusForm = ({
  patient,
  onSuccess,
  onCancel,
}: EngagementStatusFormProps) => {
  const {
    currentStatus,
    updateStatus,
    isUpdating: isUpdatingStatus,
  } = useEngagementStatus(patient, onSuccess);

  const [selectedStatus, setSelectedStatus] =
    useState<PatientEngagementStatusCode | null>(null);

  // Initialize selected status when current status is available
  useEffect(() => {
    if (currentStatus && !selectedStatus) {
      startTransition(() => {
        setSelectedStatus(currentStatus.code as PatientEngagementStatusCode);
      });
    }
  }, [currentStatus, selectedStatus]);

  const handleUpdateEngagementStatus = useCallback(async () => {
    if (!selectedStatus || !patient) return;

    const updated = await updateStatus(selectedStatus);
    if (updated) {
      onSuccess?.();
    }
  }, [selectedStatus, patient, updateStatus, onSuccess]);

  const currentEngagementStatus = currentStatus
    ? getPatientEngagementStatusDisplay(currentStatus.code)
    : "Not set";

  // Check if the status has actually changed
  const hasStatusChanged =
    selectedStatus !== null &&
    selectedStatus !==
      (currentStatus?.code as PatientEngagementStatusCode | undefined);

  return (
    <Stack gap="md">
      <Alert
        icon={<IconAlertTriangle size={16} />}
        color="yellow"
        variant="light"
      >
        <Text size="sm" mb="xs">
          Manually overriding engagement status will bypass automatic
          synchronization. This action should be used sparingly and with
          clinical justification.
        </Text>

        <Text size="sm">
          Current status: <strong>{currentEngagementStatus}</strong>
        </Text>
      </Alert>

      <FormField label="New Engagement Status" required>
        <Select
          data={PATIENT_ENGAGEMENT_STATUS_SELECT_OPTIONS}
          value={selectedStatus || null}
          onChange={(value) =>
            setSelectedStatus(value as PatientEngagementStatusCode | null)
          }
        />
      </FormField>

      <Group justify="flex-end" gap="sm">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdateEngagementStatus}
          loading={isUpdatingStatus}
          disabled={!hasStatusChanged}
        >
          Update Status
        </Button>
      </Group>
    </Stack>
  );
};
